import { useMemo } from "react";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { groupTopicDataByTopic } from "./utils/groupTopicDataByTopic";

const TOPIC_DIMENSION = { dimensionType: "topicClusteringNode", level: "topic" };
const SUBTOPIC_DIMENSION = { dimensionType: "topicClusteringNode", level: "subtopic" };

/**
 * Topic-clustering prevalence (split mode): one section per topic, its
 * sub-topics as the bars.
 *
 * Two queries, one per level, rather than one (topic, sub-topic) query: each
 * level's rows then carry their own BE-stamped share of the annotations in
 * scope, and the sub-topic rows carry their topic in `parent`, so the two
 * stitch together without touching the tree.
 *
 * An optional breakdown dimension rides on both queries as a second (third,
 * with the level) dimension, so every bar arrives with `children` — the
 * shape PrevalenceSections already draws. Parent counts stay document-unit
 * and every level carries a percentage (aggrMetrics' per-level dedupe), so
 * a breakdown changes what a bar shows, never what it counts.
 *
 * Returns groups in the shape PrevalenceSections renders, so a host can drop
 * them in alongside its taxonomy sections.
 *
 * @param vizCardIdPrefix    namespaces this host's two query caches
 * @param findQuery          optional narrowing (the widget scopes to its own
 *                           contentType + valuePath; the explorer does not)
 * @param activeFilters      the scope both levels are counted within
 * @param breakdownDimension optional — same shape as any aggrMetrics dimension
 * @param enabled            gate — callers pass their split-mode check
 */
export const useTopicPrevalence = ({
  vizCardIdPrefix,
  findQuery,
  activeFilters,
  breakdownDimension,
  enabled = false,
}) => {
  const topicDimensions = useMemo(
    () => (breakdownDimension ? [TOPIC_DIMENSION, breakdownDimension] : [TOPIC_DIMENSION]),
    [breakdownDimension]
  );
  const subtopicDimensions = useMemo(
    () => (breakdownDimension ? [SUBTOPIC_DIMENSION, breakdownDimension] : [SUBTOPIC_DIMENSION]),
    [breakdownDimension]
  );
  const {
    data: topicRows,
    status: topicStatus,
    error: topicError,
  } = useAggrMetricsAcrossDimensions({
    vizCardId: `${vizCardIdPrefix}-topics`,
    contentType: "annotations",
    dimensions: topicDimensions,
    findQuery,
    activeFilters,
    queryOptions: { enabled },
  });

  const {
    data: subtopicRows,
    status: subtopicStatus,
    error: subtopicError,
  } = useAggrMetricsAcrossDimensions({
    vizCardId: `${vizCardIdPrefix}-subtopics`,
    contentType: "annotations",
    dimensions: subtopicDimensions,
    findQuery,
    activeFilters,
    queryOptions: { enabled },
  });

  const topicGroups = useMemo(
    () => (enabled ? groupTopicDataByTopic({ topicRows, subtopicRows }) : []),
    [enabled, topicRows, subtopicRows]
  );

  const status = !enabled
    ? "success"
    : [topicStatus, subtopicStatus].some((s) => s === "error")
    ? "error"
    : [topicStatus, subtopicStatus].some((s) => s === "loading" || s === "idle")
    ? "loading"
    : "success";

  return { topicGroups, status, error: topicError || subtopicError };
};
